import { useContext, useEffect, useMemo, useState } from "react";
import { config, searchRegex } from "../../../../constant";
import { useElement, useLocale, useService } from "../../../../hooks";
import { AppContext, NavigatorContext } from "../../../../context";

export const useEventList = () => {
  const { t } = useLocale();
  const { navigateTo } = useContext(NavigatorContext);
  const { isValidJson } = useService("Validation");
  const Actor = useElement("Actor");
  const Badge = useElement("Badge");
  const Img = useElement("Img");
  const { fetchAll, remove, update, save } = useService("Data", "event");
  const { setTitle } = useService("Meta");
  const { actions: { showPreloader, hidePreloader } } = useContext(AppContext);
  const { showAlert, generateUID, getFormattedDate, isBottom } =
    useService("Misc");
  const { urlToSplitData, getLocationData } = useService("Router");
  const { commonPageSize } = config;
  const filterTags = require("../filterTags.json");
  const [tableContent, settableContent] = useState(null);
  const [tableData, settableData] = useState([]);
  const [filteredList, setfilteredList] = useState([]);
  const [checkedList, setcheckedList] = useState([]);
  const [activeFilterIndex, setactiveFilterIndex] = useState<number>(0);
  const [showTrashConfirm, setshowTrashConfirm] = useState<boolean>(false);
  const [isMultipleDelete, setisMultipleDelete] = useState<boolean>(false);
  const [checkedAll, setcheckedAll] = useState<boolean>(false);
  const [trashAction, settrashAction] = useState<boolean>(false);
  const [activeItem, setactiveItem] = useState(null);
  const [searchKey, setsearchKey] = useState<string>("");
  const [activeShownTotal, setactiveShownTotal] =
    useState<number>(commonPageSize);
  const [activeFilter, setactiveFilter] = useState(filterTags[0]);

  setTitle(t("eventsList"), "admin");
  const urlData = useMemo(
    () => urlToSplitData(getLocationData().pathname),
    [getLocationData, urlToSplitData]
  );
  useEffect(() => {
    settrashAction(urlData[2] === "trash");
    getEventsListData();
  }, []);

  const resetSearch = () => {
    setfilteredList(tableData);
    setsearchKey("");
  };

  const trackScrolling = () => {
    const wrappedElement = document.getElementById("list-wrapper");
    if (isBottom(wrappedElement)) {
      if (
        filteredList.length > commonPageSize &&
        activeShownTotal < filteredList.length
      )
        showMore();
    }
  };

  const showMore = () => {
    showPreloader();
    setTimeout(() => {
      hidePreloader();
      setactiveShownTotal(activeShownTotal + commonPageSize);
    }, 300);
  };

  const getEventsListData = async () => {
    const result = await fetchAll();
    if (result.status) {
      let tableData = [];
      result.data.forEach((e) => {
        e.image = isValidJson(e.image) ? JSON.parse(e.image) : null;
        if (!trashAction) {
          if (!e.trash) tableData.push(e);
        } else {
          if (e.trash) tableData.push(e);
        }
      });
      settableData(tableData);
      let activefilterValue = "all";
      filterTags.forEach((e) => {
        if (Number(e.id) === activeFilterIndex + 1) {
          activefilterValue = e.value;
        }
      });
      filterItems(activefilterValue, activeFilterIndex, tableData);
      if (result.data?.length === 0) {
        showAlert({ type: "error", msg: "No event added yet!" });
      }
    } else showAlert({ type: "error", msg: "No event added yet!" });
  };

  const trashCancel = () => {
    setactiveItem(null);
    setshowTrashConfirm(false);
  };

  const deleteSelectEvent = () => {
    deleteEvent(activeItem.uid);
  };

  const deleteEvent = async (uid, task = "trash") => {
    if (trashAction && task === "trash") {
      const result = await remove({ uid });
      if (result.status) {
        showAlert({ type: "success", msg: "Event deleted successfully!" });
        setactiveItem(null);
        setshowTrashConfirm(false);
        getEventsListData();
      } else showAlert({ type: "error", msg: "Unable to delete event!" });
    } else {
      let eventObject = null;
      tableData.forEach((e) => {
        if (e.uid === uid) eventObject = e;
      });
      eventObject.trash = task === "trash";
      delete eventObject._id;
      let data = {
        query: JSON.stringify({ uid }),
        eventData: JSON.stringify(eventObject),
      };
      const result = await update(data);
      if (result.status) {
        showAlert({
          type: "success",
          msg: "Event " + task + "d successfully!",
        });
        setactiveItem(null);
        setshowTrashConfirm(false);
        getEventsListData();
      } else showAlert({ type: "error", msg: "Unable to " + task + " event!" });
    }
  };

  useEffect(() => {
    setcheckedAll(filteredList.length === checkedList.length);
  }, [filteredList, checkedList]);

  const toggleCheck = (uid) => {
    if (checkedList.includes(uid)) {
      setcheckedList(checkedList.filter((e) => e !== uid));
    } else {
      setcheckedList([...checkedList, uid]);
    }
  };

  const deleteChecked = () => {
    (checkedAll ? filteredList : checkedList).forEach((e) => {
      deleteEvent(e.uid);
    });
    setcheckedList([]);
    setisMultipleDelete(false);
  };

  const cloneEvent = async (item) => {
    let eventObject = { ...item, uid: generateUID() };
    const result = await save(eventObject);
    if (result.status)
      showAlert(
        { type: "success", msg: "Event clonsed successfully!" },
        getEventsListData
      );
    else showAlert({ type: "error", msg: "Unable to add event!" });
  };

  const getTableData = (filterData) => {
    let data = [];
    filterData.forEach((item) => {
      let e = {
        checkAction: (
          <span className="check-all" onClick={() => toggleCheck(item.uid)}>
            {checkedAll || checkedList.includes(item.uid) ? (
              <i className="orange hi-check-circle"></i>
            ) : (
              <i className="hi-circle"></i>
            )}
          </span>
        ),
        title: item.title,
        image: item.image ? (
          <Img src={item.image[0]} style={{ maxHeight: 40, maxWidth: 40 }} />
        ) : (
          <p>{t("noImage")}</p>
        ),
        publishDate: getFormattedDate(new Date(item.publishDate)),
        status: <Badge item={item.status} />,
        action: trashAction ? (
          <div className="action-bar">
            <Actor
              type="trash"
              onClick={() => {
                setactiveItem(item);
                setshowTrashConfirm(true);
              }}
            />
            <Actor
              type="undo"
              onClick={() => deleteEvent(item.uid, "restore")}
            />
          </div>
        ) : (
          <div className="action-bar">
            <Actor
              type="trash"
              onClick={() => {
                setactiveItem(item);
                setshowTrashConfirm(true);
              }}
            />
            <Actor
              type="view"
              onClick={() =>
                navigateTo({ route: "/event/" + item.slug, newTab: true })
              }
            />
            <Actor
              type="edit"
              onClick={() =>
                navigateTo({ route: "/admin/event/edit/" + item.uid })
              }
            />
            <Actor type="copy" onClick={() => cloneEvent(item)} />
          </div>
        ),
      };
      data.push(e);
    });
    return data;
  };

  const filterItems = (key, activeFilterIndex = 0, tableData = []) => {
    const filteredList =
      key === "all" ? tableData : tableData.filter((e) => e.status === key);
    setfilteredList(filteredList);
    setactiveFilterIndex(activeFilterIndex);
  };

  const search = (e) => {
    if (e.target.value === "" || searchRegex.test(e.target.value)) {
      setsearchKey(e.target.value);
      let tempTitle = "",
        filteredList = [];
      tableData.forEach((e) => {
        tempTitle = e.title.toLowerCase();
        if (tempTitle.search(searchKey) !== -1) filteredList.push(e);
      });
      setfilteredList(filteredList);
    }
  };

  useEffect(() => {
    settableContent({
      headers: [
        {
          template: (
            <span className="check-all">
              <span onClick={() => setcheckedAll(!checkedAll)}>
                {checkedAll ? (
                  <i className="orange hi-check-circle"></i>
                ) : (
                  <i className="hi-circle"></i>
                )}
              </span>
            </span>
          ),
        },
        {
          label: t("title"),
          key: "title",
        },
        {
          label: t("image"),
          key: "image",
        },
        {
          label: t("publishDate"),
          key: "publishDate",
        },
        {
          label: t("status"),
          key: "status",
        },
        {
          label: t("actions"),
          key: "actions",
        },
      ],
      content: getTableData(filteredList),
    });
  }, [filteredList, checkedAll, checkedList]);
  return {
    trackScrolling,
    trashAction,
    checkedList,
    checkedAll,
    setisMultipleDelete,
    setshowTrashConfirm,
    activeFilter,
    setactiveFilter,
    filterItems,
    filterTags,
    searchKey,
    search,
    resetSearch,
    tableContent,
    activeShownTotal,
    deleteChecked,
    deleteSelectEvent,
    trashCancel,
    showTrashConfirm,
    isMultipleDelete,
  };
};
